/**
 * What one viewer has arranged in the room: where they dragged people, who they starred, and which
 * view they prefer. Kept in this browser only.
 *
 * Per viewer on purpose. A manager tidying the room for a team of fifteen should not rearrange it
 * for everybody else, and starring someone to follow is nobody's business but the person doing it.
 * Storage can be refused (private windows, blocked site data), so every read and write survives
 * that and the room works without it.
 */

import { useCallback, useState } from 'react'
import type { Overrides } from './areas'

function read<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Not saved; the arrangement lasts for this visit.
  }
}

/** A value persisted under a key, with a setter that saves as it sets. */
function usePersisted<T>(key: string, fallback: T): [T, (next: T | ((current: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => read(key, fallback))

  const set = useCallback(
    (next: T | ((current: T) => T)) => {
      setValue((current) => {
        const resolved = typeof next === 'function' ? (next as (current: T) => T)(current) : next
        write(key, resolved)
        return resolved
      })
    },
    [key],
  )

  return [value, set]
}

export type RoomView = 'room' | 'report'

export function useRoomPrefs(scope: string) {
  const [overrides, setOverrides] = usePersisted<Overrides>(`devpulse:room:${scope}:layout`, {})
  const [marked, setMarked] = usePersisted<string[]>(`devpulse:room:${scope}:marked`, [])
  const [view, setView] = usePersisted<RoomView>(`devpulse:room:${scope}:view`, 'room')

  const toggleMark = useCallback(
    (developerId: string) =>
      setMarked((current) => (current.includes(developerId) ? current.filter((id) => id !== developerId) : [...current, developerId])),
    [setMarked],
  )

  return { overrides, setOverrides, marked, toggleMark, view, setView }
}
